import type { PermissionAction, PermissionItem, PermissionModule } from '../../types';

/**
 * Excecoes de um usuario sobre o perfil dele, na grade de permissoes.
 *
 * A grade mostra o que o usuario pode de fato (perfil + excecoes). Marcar ou
 * desmarcar uma celula so cria excecao quando o novo valor difere do perfil;
 * voltar ao valor do perfil apaga a excecao. Assim nunca sobra excecao que nao
 * muda nada, e a celula destacada e sempre uma diferenca real.
 */

const chave = (module: PermissionModule, action: PermissionAction) => `${module}:${action}`;

export function permitidoNoPerfil(perfil: PermissionItem[], module: PermissionModule, action: PermissionAction): boolean {
    return perfil.find((p) => p.module === module && p.action === action)?.allowed ?? false;
}

export function valorEfetivo(
    perfil: PermissionItem[],
    excecoes: PermissionItem[],
    module: PermissionModule,
    action: PermissionAction,
): boolean {
    const excecao = excecoes.find((e) => e.module === module && e.action === action);
    return excecao ? excecao.allowed : permitidoNoPerfil(perfil, module, action);
}

/** Chaves `modulo:acao` das celulas que sao excecao, para a grade destacar. */
export function chavesDeExcecao(excecoes: PermissionItem[]): Set<string> {
    return new Set(excecoes.map((e) => chave(e.module, e.action)));
}

/** Inverte o valor efetivo de uma celula e devolve a nova lista de excecoes. */
export function alternarCelula(
    perfil: PermissionItem[],
    excecoes: PermissionItem[],
    module: PermissionModule,
    action: PermissionAction,
): PermissionItem[] {
    const novoValor = !valorEfetivo(perfil, excecoes, module, action);
    const semEsta = excecoes.filter((e) => !(e.module === module && e.action === action));
    return novoValor === permitidoNoPerfil(perfil, module, action)
        ? semEsta
        : [...semEsta, { module, action, allowed: novoValor }];
}

/**
 * Ao trocar de perfil, excecao que passou a coincidir com o perfil novo nao
 * muda mais nada e sai.
 */
export function limparExcecoesRedundantes(perfil: PermissionItem[], excecoes: PermissionItem[]): PermissionItem[] {
    return excecoes.filter((e) => e.allowed !== permitidoNoPerfil(perfil, e.module, e.action));
}
